import {
  FieldElementUnion,
  FormSchema,
  IFileInputElement,
  IFormSchemaV2,
  IRepeatableElement,
  isFileInputElement,
  isFunctionElement,
  isInputElement,
  isRepeatableElement,
  isStaticElement,
} from "@graffer-inc/form-schema";

import {
  isKintoneSubtableFormField,
  KintoneFormField,
  KintoneSubtableFormField,
} from "../kintone";
import { ElementFieldMapping } from "../kintone-export-setting";
import { KintoneExportFieldCorrespondences } from "./correspondence";

export type ValidateElementFieldMappingsProps = {
  mappings: ElementFieldMappingWithKey[];
  formSchema: IFormSchemaV2;
  kintoneFields: Record<KintoneFormField["code"], KintoneFormField>;
  usedKintoneFieldCode?: Set<KintoneFormField["code"]>;
};

export type ElementFieldMappingWithKey = ElementFieldMapping &
  ElementFieldMappingKey;

// validation error が発生した場合に、どの mapping に対してエラーが発生したかを
// 返せるように、 mapping に key を要求する。
// 通常、配列で保持されている mapping の index などでよい。
export type ElementFieldMappingKey = {
  mappingKey: string | number;
};

export type ElementFieldMappingsValidationResult = {
  errors: ElementFieldMappingValidationError[];
  usedKintoneFieldCode: Set<KintoneFormField["code"]>;
};

export type ElementFieldMappingValidationError = {
  errorCode: ElementFieldMappingValidationErrorCode;
} & ElementFieldMappingKey;

export const ERROR_CODES = {
  ELEMENT_ID_EMPTY: "ELEMENT_ID_EMPTY",
  KINTONE_FIELD_CODE_EMPTY: "KINTONE_FIELD_CODE_EMPTY",
  ELEMENT_NOT_FOUND: "ELEMENT_NOT_FOUND",
  KINTONE_FIELD_NOT_FOUND: "KINTONE_FIELD_NOT_FOUND",
  REPEATABLE_ELEMENT_DETECTED: "REPEATABLE_ELEMENT_DETECTED",
  FUNCTION_ELEMENT_DETECTED: "FUNCTION_ELEMENT_DETECTED",
  STATIC_ELEMENT_DETECTED: "STATIC_ELEMENT_DETECTED",
  TYPE_MISMATCH: "TYPE_MISMATCH",
  KINTONE_FIELD_ALREADY_USED: "KINTONE_FIELD_ALREADY_USED",
  KINTONE_SUBTABLE_ALREADY_USED: "KINTONE_SUBTABLE_ALREADY_USED",
  // repeatable 内の項目が kintone の通常フィールドにマッピングされているか、
  // あるいは通常の入力項目が kintone の SUBTABLE 内のフィールドにマッピングされているケース。
  // 適切なエラーコードがあれば書き換えたい。
  LAYER_MISMATCH: "LAYER_MISMATCH",
  // 選択系のコンポーネントにおいて選択肢に齟齬がある場合のエラーコード。
  OPTION_MISMATCH: "OPTION_MISMATCH",
  // IFormSchemaV2 側のファイル入力項目が複数回使用されている場合のエラーコード。
  // その他の入力項目の場合は、複数回使用されていても問題ないが、ファイル入力項目は
  // 次のような独自の問題が発生するため、制約を設ける。
  // https://graffer.slack.com/archives/C01VDQ6RPGC/p1706804081874449?thread_ts=1706775153.846019&cid=C01VDQ6RPGC
  FORM_SCHEMA_FILE_INPUT_ALREADY_USED: "FORM_SCHEMA_FILE_INPUT_ALREADY_USED",
} as const;

export type ElementFieldMappingValidationErrorCode =
  (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const validateElementFieldMappings = ({
  mappings,
  formSchema,
  kintoneFields,
  usedKintoneFieldCode: usedKintoneFieldCodeArg,
}: ValidateElementFieldMappingsProps): ElementFieldMappingsValidationResult => {
  const errors: ElementFieldMappingValidationError[] = [];
  const usedKintoneFieldCode: Set<KintoneFormField["code"]> =
    usedKintoneFieldCodeArg ?? new Set();

  const usedKintoneSubtable: Record<
    KintoneSubtableFormField["code"],
    IRepeatableElement["id"]
  > = {};

  const usedFormSchemaFileInput: Set<IFileInputElement["id"]> = new Set();

  const elements = FormSchema.fromJSON(formSchema)
    .flattenDeep()
    .reduce<Record<FieldElementUnion["id"], FieldElementUnion>>(
      (prev, curr) => {
        prev[curr.id] = curr;
        return prev;
      },
      {},
    );
  const kintoneFieldsWithParentCode = Object.entries(kintoneFields).reduce<
    Record<KintoneFormField["code"], KintoneFormFieldWithParentSubtableCode>
  >((prev, [key, value]) => {
    prev[key] = value;
    if (isKintoneSubtableFormField(value)) {
      Object.values(value.fields).forEach((field) => {
        prev[field.code] = {
          ...field,
          parentSubtableCode: value.code,
        };
      });
    }
    return prev;
  }, {});

  mappings.forEach((mapping) => {
    const errorCode = validateElementFieldMapping({
      mapping,
      kintoneFields: kintoneFieldsWithParentCode,
      elements,
      usedKintoneFieldCode,
      usedKintoneSubtable,
      usedFormSchemaFileInput,
    });

    if (!errorCode) {
      usedKintoneFieldCode.add(mapping.kintoneFieldCode);
      const element = elements[mapping.formElementId];
      if (isInputElement(element) && isFileInputElement(element)) {
        usedFormSchemaFileInput.add(mapping.formElementId);
      }
      return;
    }

    errors.push({
      mappingKey: mapping.mappingKey,
      errorCode,
    });
  });

  return {
    errors,
    usedKintoneFieldCode,
  };
};

export type ValidateElementFieldMappingProps = {
  mapping: ElementFieldMapping;
  elements: Record<FieldElementUnion["id"], FieldElementUnion>;
  kintoneFields: Record<
    KintoneFormField["code"],
    KintoneFormFieldWithParentSubtableCode
  >;
  usedKintoneFieldCode: Set<KintoneFormField["code"]>;
  usedKintoneSubtable: Record<
    KintoneSubtableFormField["code"],
    IRepeatableElement["id"]
  >;
  usedFormSchemaFileInput: Set<IFileInputElement["id"]>;
};

export type KintoneFormFieldWithParentSubtableCode = KintoneFormField & {
  parentSubtableCode?: KintoneSubtableFormField["code"];
};

export const validateElementFieldMapping = ({
  mapping,
  elements,
  kintoneFields,
  usedKintoneFieldCode,
  usedKintoneSubtable,
  usedFormSchemaFileInput,
}: ValidateElementFieldMappingProps):
  | ElementFieldMappingValidationErrorCode
  | undefined => {
  if (!mapping.formElementId) {
    return "ELEMENT_ID_EMPTY";
  }
  if (!mapping.kintoneFieldCode) {
    return "KINTONE_FIELD_CODE_EMPTY";
  }

  const element = elements[mapping.formElementId];
  if (!element) {
    return "ELEMENT_NOT_FOUND";
  }
  const kintoneField = kintoneFields[mapping.kintoneFieldCode];
  if (!kintoneField) {
    return "KINTONE_FIELD_NOT_FOUND";
  }

  if (usedKintoneFieldCode.has(mapping.kintoneFieldCode)) {
    return "KINTONE_FIELD_ALREADY_USED";
  }

  if (isRepeatableElement(element)) {
    return "REPEATABLE_ELEMENT_DETECTED";
  }

  if (isFunctionElement(element)) {
    return "FUNCTION_ELEMENT_DETECTED";
  }

  if (isStaticElement(element)) {
    return "STATIC_ELEMENT_DETECTED";
  }

  if (!!element.parent !== !!kintoneField.parentSubtableCode) {
    return "LAYER_MISMATCH";
  }

  if (!!element.parent && !!kintoneField.parentSubtableCode) {
    const mappedRepeatableElementId =
      usedKintoneSubtable[kintoneField.parentSubtableCode];
    if (
      mappedRepeatableElementId &&
      mappedRepeatableElementId !== element.parent.repeatable.id
    ) {
      return "KINTONE_SUBTABLE_ALREADY_USED";
    }
    usedKintoneSubtable[kintoneField.parentSubtableCode] =
      element.parent.repeatable.id;
  }

  if (
    KintoneExportFieldCorrespondences[element.inputType].every(
      (acceptedType) => acceptedType !== kintoneField.type,
    )
  ) {
    return "TYPE_MISMATCH";
  }

  if ("enum" in element) {
    if (!("options" in kintoneField)) {
      // 型のために分岐を入れているが、
      // この行より前で除外できている想定。
      return "TYPE_MISMATCH";
    }
    const kintoneOptionsSet = new Set(Object.keys(kintoneField.options));
    if (element.enum.some(({ text }) => !kintoneOptionsSet.has(text))) {
      return "OPTION_MISMATCH";
    }
  }

  if (isFileInputElement(element) && usedFormSchemaFileInput.has(element.id)) {
    return "FORM_SCHEMA_FILE_INPUT_ALREADY_USED";
  }

  return undefined;
};
